import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { setSeo, ORIGIN } from "@/seo/SeoManager";
import PostCard from "@/components/PostCard";
import { Star, MapPin, Phone, Globe, BadgeCheck, ShieldCheck, MessageSquare, Heart, Share2, Clock, Sparkles, QrCode, HelpCircle, CheckCircle2, Award, RotateCcw, Languages as LanguagesIcon, Image as ImageIcon, Ticket, Pencil, Trash2, Plus, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { STATUS_OPTIONS, statusText, statusDotColor } from "@/lib/businessStatus";
import PinButton from "@/components/PinButton";
import UploadButton from "@/components/UploadButton";
import PhotoGallery from "@/components/PhotoGallery";
import CategoryTypeahead from "@/components/CategoryTypeahead";
import ZipInput from "@/components/ZipInput";

const SUBNAV = [["feed", "Posts"], ["photos", "Photos"], ["services", "Services & Promotions"], ["reviews", "Reviews"]];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-[#222]/55 font-semibold">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export default function ProProfile() {
  const { id, section = "feed" } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, workedWith, refreshHistory } = useAuth();
  const [pro, setPro] = useState(null);
  const [posts, setPosts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [cfg, setCfg] = useState({ post_reactions: [], comment_chips: { praise: [], intent: [] } });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [msgText, setMsgText] = useState("");
  const [showMsg, setShowMsg] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [priorHires, setPriorHires] = useState([]);
  const [showHire, setShowHire] = useState(false);
  const [hireForm, setHireForm] = useState({ service_name: "", description: "", amount: "" });

  // Owner contextual editing
  const [newPost, setNewPost] = useState("");
  const [newPostImage, setNewPostImage] = useState("");
  const [svcModal, setSvcModal] = useState(null);    // { index, data:{name,description,price,duration} }
  const [promoModal, setPromoModal] = useState(null); // { index, data:{title,description,valid_until} }

  // Owner "Edit profile" mode (identity + contact basics) with live preview
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [catInput, setCatInput] = useState("");
  const [reviewSort, setReviewSort] = useState("recent");
  const [showSuggestCategory, setShowSuggestCategory] = useState(false);
  const [suggestCategoryValue, setSuggestCategoryValue] = useState("");
  const [suggestCategoryReason, setSuggestCategoryReason] = useState("");
  const [suggestingCategory, setSuggestingCategory] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [canReview, setCanReview] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const activeSection = SUBNAV.some(([s]) => s === section) ? section : "feed";

  const loadPosts = () => api.get(`/posts/by-pro/${id}`).then(r => setPosts(r.data));

  useEffect(() => {
    api.get(`/pros/${id}`).then(r => {
      setPro(r.data);
      // ?edit=profile deep-link (used by the Dashboard business status summary)
      if (searchParams.get("edit") === "profile" && user && user.id === r.data.user_id) {
        setDraft({
          ...r.data,
          languages: r.data.languages || [],
          faq: r.data.faq || [],
          categories: r.data.categories?.length ? r.data.categories : (r.data.category ? [r.data.category] : []),
          business_status: r.data.business_status || { code: "none", custom_text: "", return_date: "" },
        });
        setEditMode(true);
      }
    }).catch(() => {
      // Invalid / missing pro — keep it out of search (soft-404).
      setSeo({
        title: "Profile not found | LIMYA",
        description: "This professional profile could not be found on LIMYA.",
        canonical: `${ORIGIN}/pro/${id}`,
        robots: "noindex, follow",
      });
    });
    loadPosts();
    api.get(`/reviews/by-pro/${id}`).then(r => setReviews(r.data));
    api.get(`/hires/pro/${id}/stats`).then(r => setLoyalty(r.data)).catch(() => {});
    api.get(`/config/public`).then(r => setCfg({
      post_reactions: r.data.post_reactions || [],
      comment_chips: r.data.comment_chips || { praise: [], intent: [] },
    })).catch(() => {});
    if (user?.role === "client") {
      api.get(`/hires/by-pair/${id}`).then(r => setPriorHires(r.data || [])).catch(() => {});
      api.get(`/reviews/eligibility/${id}`).then(r => setCanReview(!!r.data.can_review)).catch(() => setCanReview(false));
    } else {
      setCanReview(false);
    }
  }, [id, user?.id]);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [activeSection]);

  // Dynamic per-profile SEO (canonical / title / description / robots), built
  // only from fields that are already public on the pro's LIMYA profile.
  useEffect(() => {
    setSeo({ canonical: `${ORIGIN}/pro/${id}` });
  }, [id]);

  useEffect(() => {
    if (!pro) return;
    const canonical = `${ORIGIN}/pro/${id}`;
    if (pro.suspended || pro.hidden) {
      setSeo({
        title: "Profile unavailable | LIMYA",
        description: "This professional is not currently listed on LIMYA.",
        canonical,
        robots: "noindex, follow",
      });
      return;
    }
    if (pro.sample_content || pro.is_example) {
      // Example/demo profiles are excluded from the sitemap and kept out of search.
      const nm = (pro.business_name || "").trim() || "Example pro";
      setSeo({
        title: `${nm} (example) | LIMYA`,
        description: "An example professional profile shown as a demo on LIMYA.",
        canonical,
        robots: "noindex, follow",
      });
      return;
    }
    const name = (pro.business_name || "").trim() || "Local pro";
    const cat = (pro.category || "").trim();
    const city = (pro.city || "").trim();
    const where = cat && city ? `${cat} in ${city}` : (cat || city);
    const title = `${name}${where ? ` — ${where}` : ""} | LIMYA`;
    const bio = (pro.bio || "").trim();
    const description = bio
      ? (bio.length > 155 ? `${bio.slice(0, 152)}…` : bio)
      : `${name}${cat ? `, ${cat}` : ""}${city ? ` in ${city}` : ""} on LIMYA — the neighborhood bulletin board. See services, reviews, and how to get in touch.`;
    setSeo({ title, description, canonical, robots: "index, follow" });
  }, [pro, id]);

  if (!pro) return <div><Header/><div className="text-center py-20 text-[#222]/50">Loading…</div></div>;

  const today = new Date().toISOString().slice(0, 10);
  const activePromos = (pro.promotions || []).filter(p => !p.valid_until || p.valid_until >= today);
  const goSection = (sec) => navigate(sec === "feed" ? `/pro/${id}` : `/pro/${id}/${sec}`);
  const isOwner = !!user && user.id === pro.user_id;

  // Build a full pro-profile payload so a targeted `services` update via
  // PUT /pros/me doesn't wipe the rest of the profile.
  const buildProfilePayload = (overrides) => ({
    business_name: pro.business_name, category: pro.category,
    categories: pro.categories && pro.categories.length ? pro.categories : (pro.category ? [pro.category] : []),
    bio: pro.bio || "",
    phone: pro.phone || "", website: pro.website || "", city: pro.city,
    service_radius: pro.service_radius || 25,
    logo_url: pro.logo_url || "", avatar_url: pro.avatar_url || "", cover_url: pro.cover_url || "",
    languages: pro.languages || [], years_in_business: pro.years_in_business || 0,
    services: pro.services || [],
    hours: pro.hours || "", response_time: pro.response_time || "Within a day",
    card_color: pro.card_color || "#FFFFFF", pin_color: pro.pin_color || "#EF4444",
    faq: pro.faq || [], address: pro.address || "", zip: pro.zip || "",
    about_image_url: pro.about_image_url || "",
    ...overrides,
  });

  const saveServices = async (services) => {
    try {
      const r = await api.put("/pros/me", buildProfilePayload({ services }));
      setPro(r.data);
      toast.success("Services saved");
    } catch { toast.error("Could not save services"); }
  };
  const savePromos = async (promotions) => {
    try {
      const r = await api.put("/pros/me/promotions", { promotions });
      setPro(p => ({ ...p, promotions: r.data.promotions || [] }));
      toast.success("Promotions saved");
    } catch { toast.error("Could not save promotions"); }
  };
  const deleteService = (i) => {
    if (!window.confirm("Remove this service?")) return;
    saveServices((pro.services || []).filter((_, idx) => idx !== i));
  };
  const deletePromo = (i) => {
    if (!window.confirm("Remove this promotion?")) return;
    savePromos((pro.promotions || []).filter((_, idx) => idx !== i));
  };
  const submitOwnerPost = async (e) => {
    e.preventDefault();
    if (!newPost.trim() && !newPostImage) { toast.error("Write something or add a photo"); return; }
    try {
      await api.post("/posts", { content: newPost, image_url: newPostImage });
      setNewPost(""); setNewPostImage("");
      loadPosts();
      toast.success("Posted to your feed");
    } catch { toast.error("Could not post"); }
  };

  // ---- Edit profile mode ----
  const view = editMode && draft ? draft : pro;   // live-preview source for header + contact rail
  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const startEdit = () => {
    setDraft({
      ...pro,
      languages: pro.languages || [],
      faq: pro.faq || [],
      categories: pro.categories && pro.categories.length ? pro.categories : (pro.category ? [pro.category] : []),
      business_status: pro.business_status || { code: "none", custom_text: "", return_date: "" },
    });
    setCatInput("");
    setEditMode(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditMode(false); setDraft(null); toast("Changes discarded"); };

  const addCategory = (val) => {
    const v = (val || "").trim();
    if (!v) return;
    const cur = draft.categories || [];
    if (cur.length >= 3) { toast.error("Up to 3 categories"); return; }
    if (cur.some(c => c.toLowerCase() === v.toLowerCase())) { setCatInput(""); return; }
    setField("categories", [...cur, v]);
    setCatInput("");
  };
  const removeCategory = (i) => setField("categories", (draft.categories || []).filter((_, idx) => idx !== i));
  const submitCategorySuggestion = async () => {
    if (!suggestCategoryValue.trim()) { toast.error("Enter a category name"); return; }
    setSuggestingCategory(true);
    try {
      await api.post("/categories/suggest", { suggested_category: suggestCategoryValue.trim(), reason: suggestCategoryReason.trim() });
      toast.success("Thanks — sent to the LIMYA team for review.");
      setSuggestCategoryValue(""); setSuggestCategoryReason(""); setShowSuggestCategory(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send suggestion");
    } finally {
      setSuggestingCategory(false);
    }
  };
  const saveProfile = async () => {
    const d = draft;
    if (!d.business_name?.trim()) { toast.error("Business name is required"); return; }
    const cats = (d.categories && d.categories.length ? d.categories : (d.category ? [d.category] : [])).slice(0, 3);
    if (cats.length === 0) { toast.error("Add at least one category"); return; }
    setSavingProfile(true);
    try {
      const r = await api.put("/pros/me", {
        business_name: d.business_name, category: cats[0], categories: cats, bio: d.bio || "",
        phone: d.phone || "", website: d.website || "", city: d.city || "",
        service_radius: Number(d.service_radius) || 25,
        logo_url: d.logo_url || "", avatar_url: d.avatar_url || "", cover_url: d.cover_url || "",
        languages: (typeof d.languages === "string" ? d.languages.split(",").map(s => s.trim()).filter(Boolean) : d.languages) || [],
        years_in_business: Number(d.years_in_business) || 0,
        services: d.services || [],
        hours: d.hours || "", response_time: d.response_time || "Within a day",
        card_color: d.card_color || "#FFFFFF", pin_color: d.pin_color || "#EF4444",
        faq: (d.faq || []).filter(f => f.q?.trim()), address: d.address || "", zip: d.zip || "",
        about_image_url: d.about_image_url || "",
      });
      let saved = r.data;
      // Business status lives on its own endpoint but is edited here only, so it
      // saves with the rest of the profile — one editing surface, one Save.
      const bs = d.business_status || { code: "none", custom_text: "", return_date: "" };
      const prevBs = pro.business_status || { code: "none", custom_text: "", return_date: "" };
      if (JSON.stringify(bs) !== JSON.stringify(prevBs)) {
        const sr = await api.put("/pros/me/business-status", {
          code: bs.code || "none", custom_text: bs.custom_text || "", return_date: bs.return_date || "",
        });
        saved = { ...saved, business_status: sr.data };
      }
      setPro(saved);
      setEditMode(false); setDraft(null);
      toast.success("Profile saved");
    } catch { toast.error("Could not save profile"); }
    finally { setSavingProfile(false); }
  };

  const requireAuth = () => {
    if (!user) { toast("Please sign in first"); navigate("/login"); return false; }
    return true;
  };
  const isFollowing = !!pro?.is_following;
  const followersCount = pro?.followers_count ?? 0;
  const toggleFollow = async () => {
    if (!requireAuth()) return;
    if (followBusy) return;
    setFollowBusy(true);
    const wasFollowing = isFollowing;
    try {
      const endpoint = wasFollowing ? "unfollow" : "follow";
      const { data } = await api.post(`/users/${endpoint}/${pro.user_id}`);
      setPro(prev => ({ ...prev, is_following: data.following, followers_count: data.followers_count }));
      toast.success(data.following ? `Following ${pro.business_name}` : `Unfollowed ${pro.business_name}`);
    } catch (e) {
      // State intentionally NOT changed on failure — retains the previous value shown to the user.
      toast.error(e.response?.data?.detail || "Could not update follow status. Please try again.");
    } finally {
      setFollowBusy(false);
    }
  };
  const share = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast("Share: " + url); }
  };
  const submitReview = async (e) => {
    e.preventDefault();
    if (!requireAuth()) return;
    if (user.role !== "client") { toast.error("Only clients can review"); return; }
    try {
      await api.post("/reviews", { pro_id: pro.user_id, rating: reviewForm.rating, comment: reviewForm.comment });
      setReviewForm({ rating: 5, comment: "" });
      const r = await api.get(`/reviews/by-pro/${id}`);
      setReviews(r.data);
      const p = await api.get(`/pros/${id}`); setPro(p.data);
      refreshHistory?.();
      toast.success("Review posted!");
    } catch (e) { toast.error("Failed to post review"); }
  };
  const sendMessage = async () => {
    if (!requireAuth()) return;
    if (user.role === "pro") { toast.error("Pros cannot message pros"); return; }
    try {
      await api.post("/messages", { to_user_id: pro.user_id, content: msgText });
      setMsgText(""); setShowMsg(false);
      toast.success("Message sent");
      navigate("/messages");
    } catch (e) { toast.error("Failed to send"); }
  };
  const submitHire = async (e) => {
    e.preventDefault();
    if (!requireAuth()) return;
    if (user.role !== "client") { toast.error("Only clients can request hires"); return; }
    try {
      await api.post("/hires", {
        pro_user_id: pro.user_id,
        service_name: hireForm.service_name,
        description: hireForm.description,
        amount: hireForm.amount ? parseFloat(hireForm.amount) : null,
      });
      setShowHire(false);
      setHireForm({ service_name: "", description: "", amount: "" });
      const r = await api.get(`/hires/by-pair/${pro.user_id}`);
      setPriorHires(r.data || []);
      toast.success("Hire request sent — they'll be notified");
      refreshHistory?.();
    } catch (e) { toast.error("Could not send hire request"); }
  };

  return (
    <div className={editMode ? "pb-24" : ""}>
      <Header/>
      <Toaster position="top-center"/>

      {editMode && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-black/10 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" data-testid="edit-save-bar">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
            <span className="text-sm text-[#222]/60 flex items-center gap-2"><Pencil size={14}/> Editing your profile</span>
            <div className="flex gap-2">
              <button onClick={cancelEdit} className="px-5 py-2 rounded-full bg-black/5 text-sm font-semibold hover:bg-black/10" data-testid="edit-cancel-btn">Cancel</button>
              <button onClick={saveProfile} disabled={savingProfile} className="btn-lime px-6 py-2 text-sm flex items-center gap-1.5 disabled:opacity-50" data-testid="edit-save-btn"><CheckCircle2 size={15}/> {savingProfile ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Hero with digital business card */}
      <section className="px-6 py-10">
        <div className="max-w-5xl mx-auto">
          {statusText(pro.business_status) && (
            <div
              className="mb-3 px-4 py-2 rounded-full bg-white border border-black/10 shadow-sm text-sm flex items-center gap-2 w-fit mx-auto"
              data-testid="profile-business-status-banner"
            >
              {statusDotColor(pro.business_status) && (
                <span className="w-2 h-2 rounded-full" style={{ background: statusDotColor(pro.business_status) }}/>
              )}
              <span className="text-[#222]/80">{statusText(pro.business_status)}</span>
            </div>
          )}
          <div className="relative paper-card no-lift rounded-2xl overflow-visible bg-white p-8 md:p-10" style={{background: view.card_color || "#fff"}}>
            <div className="push-pin" style={{"--pin": view.pin_color || "#EF4444"}}/>
            {isOwner && !editMode && (
              <button onClick={startEdit} className="absolute top-4 right-4 z-10 px-4 py-2 rounded-full bg-white border border-black/10 text-sm font-semibold flex items-center gap-1.5 hover:bg-black/5 shadow-sm" data-testid="edit-profile-btn">
                <Pencil size={14}/> Edit profile
              </button>
            )}
            {isOwner && editMode && (
              <span className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full bg-[#2E6B57]/20 text-[#245544] text-xs font-bold flex items-center gap-1.5" data-testid="edit-mode-pill">
                <Pencil size={12}/> Editing — only you see this
              </span>
            )}

            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="flex flex-col items-center gap-4">
                <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-white shadow-xl relative">
                  {view.avatar_url ? <img src={view.avatar_url} className="w-full h-full object-cover" alt=""/> : <div className="w-full h-full bg-[#2E6B57] grid place-items-center text-4xl font-bold">{view.business_name?.[0]}</div>}
                  {editMode && (
                    <label className="absolute inset-0 bg-black/40 grid place-items-center opacity-0 hover:opacity-100 transition cursor-pointer text-white text-xs font-semibold" data-testid="edit-avatar-overlay">
                      <UploadButton onUploaded={(url) => setField("avatar_url", url)} label="Change"/>
                    </label>
                  )}
                </div>
                <div className="flex gap-1.5 flex-wrap justify-center">
                  {pro.is_pro_plus && <span className="inline-flex items-center px-2 py-1 rounded-full bg-[#222321] text-[#D6B977] border border-[#D6B977] text-[10px] font-bold tracking-wide">PRO PLUS</span>}
                  {workedWith?.has(pro.user_id) && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#2E6B57] text-[#FFFFFF] text-[10px] font-bold" data-testid="profile-worked-together"><CheckCircle2 size={10}/> Worked Together</span>}
                  {loyalty?.neighborhood_trusted && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#C79B6B] text-white text-[10px] font-bold" data-testid="profile-neighborhood-trusted"><Award size={10}/> Neighborhood Trusted</span>}
                  {pro.verified && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#2E6B57]/20 text-[#245544] text-[10px] font-bold"><BadgeCheck size={10}/> Verified</span>}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {pro.sample_content && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E0A83E]/20 text-[#7A4E0B] border border-[#E0A83E] text-[10px] font-bold tracking-[0.12em]" data-testid="profile-example-badge">
                      EXAMPLE PROFILE
                    </span>
                  )}
                  <p className="text-xs uppercase tracking-[0.2em] text-[#222]/60 font-medium">{(view.categories && view.categories.length ? view.categories : (view.category ? [view.category] : [])).join(" · ")}</p>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mt-1 mb-3" style={{fontFamily:'Outfit'}}>{view.business_name}</h1>
                {pro.sample_content && (
                  <p className="text-xs text-[#222]/60 -mt-2 mb-3" data-testid="profile-example-note">
                    This is an example profile shown to demonstrate how LIMYA works — not a participating business.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-3 text-sm text-[#222]/70 mb-5">
                  <span className="flex items-center gap-1"><Star size={16} className="fill-[#F59E0B] text-[#F59E0B]"/><b>{pro.rating?.toFixed(1)}</b> ({pro.review_count} reviews)</span>
                  <span>·</span>
                  <span className="flex items-center gap-1"><MapPin size={14}/>{view.city}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Clock size={14}/>{view.response_time}</span>
                  <span>·</span>
                  <span>{view.years_in_business} yrs in business</span>
                </div>
                <p className="text-[#222]/80 leading-relaxed mb-5">{view.bio}</p>
                {/* Viewer-only actions. Owners get the Edit controls instead —
                    never Contact/Follow/Share on their own profile. */}
                {!editMode && !isOwner && (
                <div className="flex flex-wrap gap-2" data-testid="profile-viewer-actions">
                  {user?.role === "client" && (
                    <button onClick={() => setShowHire(true)} className="btn-action px-6 py-2.5 text-sm flex items-center gap-2" data-testid="profile-hire-btn">
                      {priorHires.length > 0 ? <><RotateCcw size={15}/> Hire Again</> : <><Sparkles size={15}/> Request Hire</>}
                    </button>
                  )}
                  <button onClick={() => setShowMsg(true)} className="btn-dark px-5 py-2.5 text-sm flex items-center gap-2" data-testid="profile-message-btn"><MessageSquare size={15}/> Contact</button>
                  <button
                    onClick={toggleFollow}
                    disabled={followBusy}
                    aria-pressed={isFollowing}
                    aria-label={isFollowing ? `Unfollow ${pro.business_name}` : `Follow ${pro.business_name}`}
                    className={`px-4 py-2.5 rounded-full border text-sm flex items-center gap-2 transition disabled:opacity-60 disabled:cursor-not-allowed ${isFollowing ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100" : "bg-white border-black/10 hover:bg-black/5"}`}
                    data-testid="profile-follow-btn"
                  >
                    <Heart size={15} className={isFollowing ? "fill-red-500 text-red-500" : ""}/>
                    {isFollowing ? "Unfollow" : "Follow"}
                    {followersCount > 0 && <span className="text-xs opacity-60" data-testid="profile-follower-count">({followersCount})</span>}
                  </button>
                  {user?.role === "client" && <PinButton pro={pro} size="lg" data-testid="profile-pin-btn"/>}
                  <button onClick={share} className="px-4 py-2.5 rounded-full bg-white border border-[#D3DAD5] text-sm flex items-center gap-2 hover:bg-black/5" data-testid="profile-share-btn"><Share2 size={15}/> Share</button>
                  {pro.is_pro_plus && (
                    <button onClick={() => setShowQR(true)} className="px-4 py-2.5 rounded-full bg-white border border-[#D3DAD5] text-sm flex items-center gap-2 hover:bg-black/5" data-testid="profile-qr-btn"><QrCode size={15}/> QR</button>
                  )}
                </div>
                )}

                {loyalty && (loyalty.completed_jobs > 0 || loyalty.neighborhood_trusted) && (
                  <div className="mt-4 flex flex-wrap gap-3 text-xs text-[#222]/70" data-testid="profile-loyalty-stats">
                    <span><b className="text-[#222]">{loyalty.completed_jobs}</b> completed {loyalty.completed_jobs === 1 ? "job" : "jobs"}</span>
                    <span>·</span>
                    <span><b className="text-[#222]">{loyalty.lifetime_clients}</b> lifetime {loyalty.lifetime_clients === 1 ? "client" : "clients"}</span>
                    {loyalty.hire_again_pct > 0 && <><span>·</span><span><b className="text-[#222]">{loyalty.hire_again_pct}%</b> hire-again rate</span></>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section nav — real routes */}
      {!editMode && (
      <div className="sticky top-0 z-20 bg-[#F2F4F2]/90 backdrop-blur border-b border-black/5 px-6">
        <div className="max-w-5xl mx-auto flex gap-1 overflow-x-auto">
          {SUBNAV.map(([sec, label]) => {
            const count = sec === "reviews" ? pro.review_count : 0;
            const active = activeSection === sec;
            return (
              <button key={sec} onClick={() => goSection(sec)}
                className={`px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition ${active ? "border-[#2E6B57] text-[#222]" : "border-transparent text-[#222]/50 hover:text-[#222]"}`}
                data-testid={`nav-${sec}`}>{label}{count ? ` (${count})` : ""}</button>
            );
          })}
        </div>
      </div>
      )}

      {/* Body: sticky contact rail + section content */}
      <section className="px-6 py-8">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left rail (always visible) */}
          <aside className="lg:col-span-1 space-y-4 lg:sticky lg:top-16 lg:self-start" data-testid="profile-contact-rail">
            <div className={`bg-white rounded-2xl p-5 shadow-sm border space-y-3 text-sm ${editMode ? "border-[#2E6B57]/60 ring-1 ring-[#2E6B57]/40" : "border-black/5"}`}>
              <h3 className="font-bold text-base" style={{fontFamily:'Outfit'}}>Contact</h3>
              {!editMode && (
                <button onClick={() => setShowMsg(true)} className="btn-lime w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="rail-message-btn">
                  <MessageSquare size={15}/> Send a message
                </button>
              )}
              {view.phone && <p className="flex items-center gap-3"><Phone size={15} className="text-[#222]/50"/> {view.phone}</p>}
              {view.website && <span className="flex items-center gap-3 text-[#245544] break-all"><Globe size={15} className="text-[#222]/50 shrink-0"/> {view.website}</span>}
              <p className="flex items-center gap-3"><MapPin size={15} className="text-[#222]/50"/> {view.city} · {view.service_radius} mi radius</p>
            </div>

            <div className={`bg-white rounded-2xl p-5 shadow-sm border space-y-3 text-sm ${editMode ? "border-[#2E6B57]/60 ring-1 ring-[#2E6B57]/40" : "border-black/5"}`}>
              <div className="flex items-start gap-3">
                <Clock size={15} className="text-[#222]/50 mt-0.5"/>
                <div><p className="font-semibold">Hours</p><p className="text-[#222]/70">{view.hours || "Not listed"}</p></div>
              </div>
              <div className="flex items-start gap-3">
                <LanguagesIcon size={15} className="text-[#222]/50 mt-0.5"/>
                <div><p className="font-semibold">Languages</p><p className="text-[#222]/70">{(Array.isArray(view.languages) ? view.languages : String(view.languages || "").split(",").map(s => s.trim()).filter(Boolean)).join(", ") || "—"}</p></div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden h-52">
              <iframe
                title={`Map of ${pro.city}`}
                width="100%" height="100%" style={{border:0}}
                loading="lazy"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=-74.05,40.6,-73.85,40.85&amp;layer=mapnik&amp;marker=40.72,-73.95`}
                data-testid="contact-map"
              />
            </div>
          </aside>

          {/* Right: active section */}
          <div className="lg:col-span-2 space-y-5">
            {editMode && (
              <div className="space-y-4" data-testid="edit-profile-panel">
                <p className="text-xs text-[#222]/50 flex items-center gap-1.5"><Pencil size={12}/> Only you can see this — your changes go live when you Save.</p>

                {/* Identity */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 space-y-4">
                  <h3 className="font-bold text-lg" style={{fontFamily:'Outfit'}}>Business card</h3>
                  <Field label="Business name">
                    <input maxLength={40} value={draft.business_name || ""} onChange={e => setField("business_name", e.target.value)} className="edit-input" data-testid="edit-business-name"/>
                    <span className="text-[11px] text-[#222]/40 float-right mt-0.5">{(draft.business_name || "").length}/40</span>
                  </Field>
                  <Field label="Primary Category & Additional Services (up to 3)">
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {(draft.categories || []).map((c, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#2E6B57]/20 text-[#245544] text-xs font-semibold" data-testid={`edit-cat-chip-${i}`}>
                          {i === 0 && <span className="text-[9px] uppercase tracking-wider opacity-60">primary</span>}
                          {c}
                          <button type="button" onClick={() => removeCategory(i)} className="hover:text-red-500" data-testid={`edit-cat-remove-${i}`}><X size={12}/></button>
                        </span>
                      ))}
                    </div>
                    {(draft.categories || []).length < 3 && (
                      <>
                        <CategoryTypeahead
                          value={catInput}
                          onChange={setCatInput}
                          onSelect={(name) => addCategory(name)}
                          onNotFound={(q) => { setSuggestCategoryValue(q); setShowSuggestCategory(true); toast.error("Not in our category list yet — suggest it below."); }}
                          placeholder="Type a category, e.g. Painting"
                          testId="edit-category-input"
                        />
                        <span className="text-[11px] text-[#222]/40">The <b>first</b> is your <b>Primary Category</b> (shown prominently &amp; used in search). The rest are <b>Additional Services</b> — searchable tags that widen discovery.</span>
                      </>
                    )}
                    <button type="button" onClick={() => setShowSuggestCategory(!showSuggestCategory)} className="text-xs text-[#245544] underline mt-2" data-testid="suggest-category-toggle">
                      Don't see your trade? Suggest a category
                    </button>
                    {showSuggestCategory && (
                      <div className="mt-2 p-3 bg-black/[0.03] rounded-xl space-y-2" data-testid="suggest-category-panel">
                        <input value={suggestCategoryValue} onChange={e => setSuggestCategoryValue(e.target.value)}
                          placeholder="Category name, e.g. Pool Maintenance" className="edit-input" data-testid="suggest-category-input" />
                        <input value={suggestCategoryReason} onChange={e => setSuggestCategoryReason(e.target.value)}
                          placeholder="Why is this needed? (optional)" className="edit-input" data-testid="suggest-category-reason-input" />
                        <button type="button" onClick={submitCategorySuggestion} disabled={suggestingCategory}
                          className="text-xs px-4 py-1.5 rounded-full bg-[#222] text-white font-semibold disabled:opacity-50" data-testid="suggest-category-submit">
                          {suggestingCategory ? "Sending…" : "Send suggestion"}
                        </button>
                      </div>
                    )}
                  </Field>
                  <Field label="About photo (optional)">
                    <div className="flex items-center gap-3">
                      {draft.about_image_url
                        ? <img src={draft.about_image_url} alt="" className="w-24 h-20 object-cover rounded-lg border border-black/10" data-testid="edit-about-image-preview"/>
                        : <div className="w-24 h-20 rounded-lg bg-[#F2F4F2] border border-black/10 grid place-items-center text-[#222]/35"><ImageIcon size={18}/></div>}
                      <div className="flex flex-col gap-1.5">
                        <UploadButton onUploaded={(url) => setField("about_image_url", url)} label={draft.about_image_url ? "Change photo" : "Add photo"}/>
                        {draft.about_image_url && (
                          <button type="button" onClick={() => setField("about_image_url", "")}
                            className="text-xs text-red-600 hover:underline text-left" data-testid="edit-about-image-remove">Remove photo</button>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] text-[#222]/40">Shown beside your About text. Free for every Professional.</span>
                  </Field>
                  <Field label="Business status">
                    <select value={draft.business_status?.code || "none"}
                      onChange={e => setField("business_status", { ...(draft.business_status || {}), code: e.target.value })}
                      className="edit-input" data-testid="edit-business-status-select">
                      <option value="none">— No banner —</option>
                      {STATUS_OPTIONS.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </select>
                    {draft.business_status?.code === "custom" && (
                      <input maxLength={80} value={draft.business_status?.custom_text || ""}
                        onChange={e => setField("business_status", { ...draft.business_status, custom_text: e.target.value })}
                        placeholder="Custom message (max 80 chars)" className="edit-input mt-2" data-testid="edit-business-status-custom-text"/>
                    )}
                    {draft.business_status?.code === "closed_until" && (
                      <input value={draft.business_status?.return_date || ""}
                        onChange={e => setField("business_status", { ...draft.business_status, return_date: e.target.value })}
                        placeholder="e.g. August 15" className="edit-input mt-2" data-testid="edit-business-status-return-date"/>
                    )}
                    <span className="text-[11px] text-[#222]/40">{statusText(draft.business_status) || "Shown as a subtle banner on your profile + a dot on your card."}</span>
                  </Field>
                  <Field label="Short bio / tagline">
                    <textarea rows={3} maxLength={140} value={draft.bio || ""} onChange={e => setField("bio", e.target.value)} className="edit-input" data-testid="edit-bio"/>
                    <span className="text-[11px] text-[#222]/40 float-right mt-0.5">{(draft.bio || "").length}/140</span>
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Years in business">
                      <input type="number" value={draft.years_in_business || 0} onChange={e => setField("years_in_business", e.target.value)} className="edit-input" data-testid="edit-years"/>
                    </Field>
                    <Field label="Response time">
                      <input value={draft.response_time || ""} onChange={e => setField("response_time", e.target.value)} placeholder="Within 2 hours" className="edit-input" data-testid="edit-response"/>
                    </Field>
                  </div>
                  <div className="flex gap-6">
                    <Field label="Card color">
                      <input type="color" value={draft.card_color || "#FFFFFF"} onChange={e => setField("card_color", e.target.value)} className="w-14 h-10 rounded-lg border border-black/10 cursor-pointer" data-testid="edit-card-color"/>
                    </Field>
                    <Field label="Pin color">
                      <input type="color" value={draft.pin_color || "#EF4444"} onChange={e => setField("pin_color", e.target.value)} className="w-14 h-10 rounded-lg border border-black/10 cursor-pointer" data-testid="edit-pin-color"/>
                    </Field>
                  </div>
                </div>

                {/* Contact & area */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 space-y-4">
                  <h3 className="font-bold text-lg" style={{fontFamily:'Outfit'}}>Contact &amp; area</h3>
                  <Field label="Phone">
                    <input value={draft.phone || ""} onChange={e => setField("phone", e.target.value)} className="edit-input" data-testid="edit-phone"/>
                  </Field>
                  <Field label="Website">
                    <input value={draft.website || ""} onChange={e => setField("website", e.target.value)} placeholder="yourbusiness.com" className="edit-input" data-testid="edit-website"/>
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="City">
                      <input value={draft.city || ""} onChange={e => setField("city", e.target.value)} className="edit-input" data-testid="edit-city"/>
                    </Field>
                    <Field label="Business ZIP (for the Wall)">
                      <ZipInput value={draft.zip || ""} onChange={v => setField("zip", v)}
                        onValid={(info) => { if (!draft.city && info.city) setField("city", info.city); }}
                        testId="edit-zip"/>
                    </Field>
                  </div>
                  <p className="text-[11px] text-[#222]/40 -mt-2">Your Wall posts appear to neighbors within their chosen radius of this ZIP.</p>
                  <Field label="Service radius (mi)">
                    <input type="number" value={draft.service_radius || 25} onChange={e => setField("service_radius", e.target.value)} className="edit-input" data-testid="edit-radius"/>
                  </Field>
                </div>

                {/* Hours & languages */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 space-y-4">
                  <h3 className="font-bold text-lg" style={{fontFamily:'Outfit'}}>Hours &amp; languages</h3>
                  <Field label="Hours">
                    <input value={draft.hours || ""} onChange={e => setField("hours", e.target.value)} placeholder="Mon–Fri 8am–6pm" className="edit-input" data-testid="edit-hours"/>
                  </Field>
                  <Field label="Languages (comma separated)">
                    <input value={Array.isArray(draft.languages) ? draft.languages.join(", ") : (draft.languages || "")} onChange={e => setField("languages", e.target.value)} placeholder="English, Spanish" className="edit-input" data-testid="edit-languages"/>
                  </Field>
                </div>
              </div>
            )}

            {!editMode && activeSection === "feed" && (
              <>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="about-section">
                  <h3 className="font-bold text-lg mb-2" style={{fontFamily:'Outfit'}}>About</h3>
                  <div className="sm:flex sm:gap-5 sm:items-start">
                    <p className="text-[#222]/80 leading-relaxed flex-1">{pro.bio || "No bio yet."}</p>
                    {pro.about_image_url && (
                      <img src={pro.about_image_url} alt={`${pro.business_name} at work`}
                        className="mt-4 sm:mt-0 w-full sm:w-56 h-44 sm:h-40 object-cover rounded-xl border border-black/5 shrink-0"
                        data-testid="about-image"/>
                    )}
                  </div>
                </div>

                {isOwner && (
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5" data-testid="owner-post-composer">
                    <form onSubmit={submitOwnerPost} className="space-y-3">
                      <div className="flex items-center gap-3">
                        {pro.avatar_url
                          ? <img src={pro.avatar_url} className="w-9 h-9 rounded-full object-cover" alt=""/>
                          : <div className="w-9 h-9 rounded-full bg-[#2E6B57] grid place-items-center font-bold">{pro.business_name?.[0]}</div>}
                        <input value={newPost} onChange={e => setNewPost(e.target.value)}
                          placeholder="Share an update with your neighbors…"
                          className="flex-1 px-4 py-2.5 rounded-full bg-[#F2F4F2] border border-black/10 text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
                          data-testid="owner-post-input"/>
                      </div>
                      {newPostImage && (
                        <div className="relative inline-block ml-12">
                          <img src={newPostImage} alt="" className="h-28 rounded-lg object-cover"/>
                          <button type="button" onClick={() => setNewPostImage("")} className="absolute -top-2 -right-2 bg-black text-white rounded-full p-1" data-testid="owner-post-image-remove"><X size={12}/></button>
                        </div>
                      )}
                      <div className="flex justify-between items-center pl-12">
                        <UploadButton onUploaded={(url) => setNewPostImage(url)} label="Add photo"/>
                        <button type="submit" className="btn-lime px-5 py-2 text-sm flex items-center gap-1.5" data-testid="owner-post-submit"><Plus size={14}/> Post</button>
                      </div>
                    </form>
                  </div>
                )}

                <h3 className="font-bold text-lg pt-2" style={{fontFamily:'Outfit'}}>Posts</h3>
                {posts.map(p => (
                  <PostCard key={p.id} post={p} author={pro} reactions={cfg.post_reactions} chips={cfg.comment_chips} onChanged={loadPosts} editable={isOwner} context="profile"/>
                ))}
                {posts.length === 0 && (
                  <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-black/10 text-[#222]/50">
                    No posts yet. Check back soon.
                  </div>
                )}
              </>
            )}

            {!editMode && activeSection === "photos" && (
              <PhotoGallery proId={id} isOwner={isOwner} />
            )}

            {!editMode && activeSection === "services" && (
              <>
                {(activePromos.length > 0 || isOwner) && (
                  <div className="mb-2" data-testid="promotions-section">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-2xl font-bold flex items-center gap-2" style={{fontFamily:'Outfit'}}><Ticket size={20} className="text-[#C79B6B]"/> Promotions</h2>
                      {isOwner && (
                        <button onClick={() => setPromoModal({ index: -1, data: { title: "", description: "", valid_until: "" } })} className="px-4 py-2 rounded-full bg-white border border-black/10 text-sm flex items-center gap-1.5 hover:bg-black/5" data-testid="owner-add-promo-btn"><Plus size={15}/> Create promotion</button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {(isOwner ? (pro.promotions || []) : activePromos).map((pm, i) => (
                        <div key={pm.id || i} className="rounded-2xl p-5 border border-[#C79B6B]/40 bg-[#FBF6EF] flex items-start gap-3" data-testid={`promo-${pm.id || i}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#C79B6B] text-white">Deal</span>
                              <h4 className="font-bold text-lg">{pm.title}</h4>
                            </div>
                            {pm.description && <p className="text-sm text-[#222]/70 mt-1">{pm.description}</p>}
                            {pm.valid_until && <p className="text-xs text-[#222]/50 mt-2">Valid until {new Date(pm.valid_until).toLocaleDateString()}</p>}
                          </div>
                          {isOwner && (
                            <div className="flex items-center gap-1 shrink-0">
                              <button onClick={() => setPromoModal({ index: i, data: { title: pm.title || "", description: pm.description || "", valid_until: pm.valid_until || "" } })} className="p-1.5 text-[#222]/40 hover:text-[#245544]" data-testid={`promo-edit-${i}`}><Pencil size={15}/></button>
                              <button onClick={() => deletePromo(i)} className="p-1.5 text-[#222]/40 hover:text-red-500" data-testid={`promo-delete-${i}`}><Trash2 size={15}/></button>
                            </div>
                          )}
                        </div>
                      ))}
                      {isOwner && (pro.promotions || []).length === 0 && (
                        <p className="text-sm text-[#222]/50">No promotions yet — create one to feature a limited-time deal.</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2">
                  <h2 className="text-2xl font-bold" style={{fontFamily:'Outfit'}}>Services</h2>
                  {isOwner && (
                    <button onClick={() => setSvcModal({ index: -1, data: { name: "", description: "", price: "", duration: "" } })} className="px-4 py-2 rounded-full bg-white border border-black/10 text-sm flex items-center gap-1.5 hover:bg-black/5" data-testid="owner-add-service-btn"><Plus size={15}/> Add service</button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {(pro.services || []).map((s, i) => (
                    <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 relative" data-testid={`service-${i}`}>
                      {isOwner && (
                        <div className="absolute top-3 right-3 flex items-center gap-1">
                          <button onClick={() => setSvcModal({ index: i, data: { name: s.name || "", description: s.description || "", price: s.price || "", duration: s.duration || "" } })} className="p-1.5 text-[#222]/40 hover:text-[#245544]" data-testid={`service-edit-${i}`}><Pencil size={15}/></button>
                          <button onClick={() => deleteService(i)} className="p-1.5 text-[#222]/40 hover:text-red-500" data-testid={`service-delete-${i}`}><Trash2 size={15}/></button>
                        </div>
                      )}
                      <h4 className="font-bold text-lg pr-16">{s.name}</h4>
                      <p className="text-sm text-[#222]/60 mt-1 mb-3">{s.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-[#245544]">${s.price}</span>
                        <span className="text-xs text-[#222]/50">{s.duration}</span>
                      </div>
                    </div>
                  ))}
                  {(!pro.services || pro.services.length === 0) && <p className="text-[#222]/50">No services listed yet.</p>}
                </div>
              </>
            )}

            {!editMode && activeSection === "reviews" && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h2 className="text-2xl font-bold flex items-center gap-3" style={{fontFamily:'Outfit'}}>
                    Reviews
                    {pro.review_count > 0 && (
                      <span className="text-base font-semibold text-[#222]/60 flex items-center gap-1">
                        <Star size={16} className="fill-[#F59E0B] text-[#F59E0B]"/> {pro.rating?.toFixed(1)} · {pro.review_count}
                      </span>
                    )}
                  </h2>
                  {reviews.length > 1 && (
                    <select value={reviewSort} onChange={e => setReviewSort(e.target.value)}
                      className="px-3 py-1.5 rounded-full border border-black/10 bg-white text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="review-sort">
                      <option value="recent">Most recent</option>
                      <option value="high">Highest rated</option>
                      <option value="low">Lowest rated</option>
                    </select>
                  )}
                </div>
                <div className="space-y-4">
                  {[...reviews].sort((a, b) =>
                    reviewSort === "high" ? b.rating - a.rating
                    : reviewSort === "low" ? a.rating - b.rating
                    : new Date(b.created_at) - new Date(a.created_at)
                  ).map(r => (
                    <div key={r.id} className="bg-white rounded-2xl p-5 shadow-sm border border-black/5" data-testid={`review-${r.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#F2F4F2] grid place-items-center font-bold">{r.client_name?.[0]}</div>
                          <div>
                            <p className="font-semibold text-sm">{r.client_name}</p>
                            <p className="text-xs text-[#222]/50">{new Date(r.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} size={14} className={i<=r.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-black/15"}/>)}</div>
                      </div>
                      <p className="text-[#222]/80 text-sm">{r.comment}</p>
                    </div>
                  ))}
                  {reviews.length===0 && <p className="text-[#222]/50">No reviews yet.{canReview && !isOwner ? " Be the first!" : ""}</p>}
                </div>
                {canReview && !isOwner ? (
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
                    <h4 className="font-bold mb-3">Leave a review</h4>
                    <form onSubmit={submitReview} className="space-y-3">
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(i => (
                          <button type="button" key={i} onClick={() => setReviewForm({...reviewForm, rating:i})} data-testid={`review-star-${i}`}>
                            <Star size={22} className={i<=reviewForm.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-black/20"}/>
                          </button>
                        ))}
                      </div>
                      <textarea required value={reviewForm.comment} onChange={e=>setReviewForm({...reviewForm, comment:e.target.value})}
                        placeholder="Share your experience…" rows={4}
                        className="w-full px-3 py-2 border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57] text-sm"
                        data-testid="review-comment-input"/>
                      <button type="submit" className="btn-lime w-full py-2.5 text-sm" data-testid="review-submit-btn">Post review</button>
                    </form>
                  </div>
                ) : !isOwner && (
                  <div className="bg-white/60 rounded-2xl p-4 border border-dashed border-black/10 text-center text-sm text-[#222]/60" data-testid="review-locked-note">
                    {user
                      ? "Reviews come from neighbors who worked with this pro through LIMYA."
                      : (
                        <>Want to leave a review? <button onClick={() => navigate("/login")} className="text-[#245544] font-semibold underline" data-testid="review-login-link">Sign in as a client</button>.</>
                      )}
                  </div>
                )}
                {(pro.faq || []).length > 0 && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2" style={{fontFamily:'Outfit'}}><HelpCircle size={18}/> Frequently asked</h3>
                    <div className="space-y-4">
                      {(pro.faq || []).map((f, i) => (
                        <div key={i} className="border-b border-black/5 pb-4 last:border-0">
                          <p className="font-semibold text-[#222]">{f.q}</p>
                          <p className="text-sm text-[#222]/70 mt-1">{f.a}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Hire modal */}
      {showHire && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowHire(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-1" style={{fontFamily:'Outfit'}}>{priorHires.length > 0 ? "Hire Again" : "Request a Hire"}</h3>
            <p className="text-sm text-[#222]/60 mb-4">
              {priorHires.length > 0
                ? `You've worked with ${pro.business_name} ${priorHires.length} time${priorHires.length>1?"s":""}. Send a new request.`
                : `Send ${pro.business_name} the details — they'll confirm.`}
            </p>
            <form onSubmit={submitHire} className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Service</label>
                <input required value={hireForm.service_name} onChange={e=>setHireForm({...hireForm, service_name:e.target.value})}
                  placeholder="e.g. Kitchen leak repair"
                  className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  data-testid="hire-service-input"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Details</label>
                <textarea rows={3} value={hireForm.description} onChange={e=>setHireForm({...hireForm, description:e.target.value})}
                  placeholder="Date, location, scope…"
                  className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  data-testid="hire-description-input"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Estimated budget (optional)</label>
                <input type="number" min={0} value={hireForm.amount} onChange={e=>setHireForm({...hireForm, amount:e.target.value})}
                  placeholder="$"
                  className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  data-testid="hire-amount-input"/>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowHire(false)} className="px-4 py-2 text-sm" data-testid="hire-cancel">Cancel</button>
                <button type="submit" className="btn-lime px-5 py-2 text-sm" data-testid="hire-submit">Send request</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR modal (Pro Plus) */}
      {showQR && pro.is_pro_plus && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-1" style={{fontFamily:'Outfit'}}>{pro.business_name}</h3>
            <p className="text-xs text-[#222]/50 mb-4 uppercase tracking-wider">Scan to view profile</p>
            <img src={`${BACKEND}/api/qr/${pro.user_id}`} alt="QR code" className="w-56 h-56 mx-auto" data-testid="pro-qr-image"/>
            <p className="handwritten text-xl text-[#C79B6B] mt-3">— {pro.category} —</p>
            <button onClick={() => setShowQR(false)} className="mt-4 px-5 py-2 text-sm bg-black/5 rounded-full" data-testid="qr-modal-close">Close</button>
          </div>
        </div>
      )}

      {/* Message modal */}
      {showMsg && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowMsg(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e=>e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-1" style={{fontFamily:'Outfit'}}>Message {pro.business_name}</h3>
            <p className="text-sm text-[#222]/60 mb-4">They typically respond {pro.response_time?.toLowerCase()}.</p>
            <textarea rows={4} value={msgText} onChange={e=>setMsgText(e.target.value)}
              placeholder="Hi! I'm interested in…"
              className="w-full px-3 py-2 border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57] text-sm"
              data-testid="message-modal-input"/>
            <div className="flex gap-2 mt-3 justify-end">
              <button onClick={() => setShowMsg(false)} className="px-4 py-2 text-sm" data-testid="message-modal-cancel">Cancel</button>
              <button onClick={sendMessage} disabled={!msgText.trim()} className="btn-action px-5 py-2 text-sm disabled:opacity-50" data-testid="message-modal-send">Send</button>
            </div>
          </div>
        </div>
      )}

      {/* Service editor modal (owner) */}
      {svcModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setSvcModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4" style={{fontFamily:'Outfit'}}>{svcModal.index === -1 ? "Add service" : "Edit service"}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Service name</label>
                <input value={svcModal.data.name} onChange={e => setSvcModal({ ...svcModal, data: { ...svcModal.data, name: e.target.value } })}
                  placeholder="e.g. EV charger install"
                  className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="svc-name-input"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Description</label>
                <textarea rows={2} value={svcModal.data.description} onChange={e => setSvcModal({ ...svcModal, data: { ...svcModal.data, description: e.target.value } })}
                  className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="svc-desc-input"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Price ($)</label>
                  <input value={svcModal.data.price} onChange={e => setSvcModal({ ...svcModal, data: { ...svcModal.data, price: e.target.value } })}
                    placeholder="450" className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="svc-price-input"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Duration</label>
                  <input value={svcModal.data.duration} onChange={e => setSvcModal({ ...svcModal, data: { ...svcModal.data, duration: e.target.value } })}
                    placeholder="e.g. 2 hrs" className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="svc-duration-input"/>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <button onClick={() => setSvcModal(null)} className="px-4 py-2 text-sm" data-testid="svc-cancel">Cancel</button>
              <button onClick={() => {
                if (!svcModal.data.name.trim()) { toast.error("Add a service name"); return; }
                const list = [...(pro.services || [])];
                if (svcModal.index === -1) list.push(svcModal.data);
                else list[svcModal.index] = svcModal.data;
                saveServices(list);
                setSvcModal(null);
              }} className="btn-lime px-5 py-2 text-sm" data-testid="svc-save">Save changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Promotion editor modal (owner) */}
      {promoModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setPromoModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4" style={{fontFamily:'Outfit'}}>{promoModal.index === -1 ? "Create promotion" : "Edit promotion"}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Title</label>
                <input value={promoModal.data.title} onChange={e => setPromoModal({ ...promoModal, data: { ...promoModal.data, title: e.target.value } })}
                  placeholder="e.g. 15% off panel upgrades"
                  className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="promo-title-input"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Details</label>
                <textarea rows={2} value={promoModal.data.description} onChange={e => setPromoModal({ ...promoModal, data: { ...promoModal.data, description: e.target.value } })}
                  className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="promo-desc-input"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Valid until (optional)</label>
                <input type="date" value={promoModal.data.valid_until} onChange={e => setPromoModal({ ...promoModal, data: { ...promoModal.data, valid_until: e.target.value } })}
                  className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="promo-date-input"/>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <button onClick={() => setPromoModal(null)} className="px-4 py-2 text-sm" data-testid="promo-cancel">Cancel</button>
              <button onClick={() => {
                if (!promoModal.data.title.trim()) { toast.error("Add a title"); return; }
                const list = [...(pro.promotions || [])];
                if (promoModal.index === -1) list.push(promoModal.data);
                else list[promoModal.index] = { ...list[promoModal.index], ...promoModal.data };
                savePromos(list);
                setPromoModal(null);
              }} className="btn-lime px-5 py-2 text-sm" data-testid="promo-save">Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
